import { Suspense } from 'react';
import Loader from './Loader';
import Modal from './Modal';
import AllUserProfilesResource from '../resources/AllUserProfiles';
import useCopy from '../util/useCopy';
import OneBallotResource from '../resources/Ballot/One';
import { BallotPreview } from './BallotPreview';
import { Header } from './Header';
import { FavoritesBar } from './FavoritesBar';
import { CurrentUserMustResource } from '../resources/Auth/CurrentUserMust';
import { getNumberSuffix, pluralize } from '@siteless/util';
import TrophyIcon from './TrophyIcon';
import ObjectiveStateResource from '../resources/ObjectiveState';
import { LetterboxdBar } from './LetterboxdBar';
import { oscarDatabase } from '../config/constants';
import CrownIcon from './CrownIcon';

let ViewBallotModalContent = ({
  ballotKey,
  score,
  strictScore,
  scoreIsStrict,
  rank,
  isOwner,
}: {
  ballotKey: string;
  scoreIsStrict?: boolean;
  score?: number;
  strictScore?: number;
  rank?: number;
  isOwner?: boolean;
}) => {
  let copy = useCopy();
  let ballot = OneBallotResource.use(ballotKey);
  let allEnums = AllUserProfilesResource.use();
  let currentUser = CurrentUserMustResource.use();
  let objectiveState = ObjectiveStateResource.use();

  let ballotMessage =
    currentUser.id === ballotKey
      ? copy.yourBallot
      : copy.getOtherUserBallotMessage({
          handle: allEnums?.users?.[ballotKey]?.handle || '',
        });

  let showScore = typeof score === 'number' && typeof rank === 'number';

  return (
    <>
      <div className="sticky top-0 z-10 bg-white">
        <Header className="mx-4 border-b-4 border-neutral-100 px-2 py-4">
          {ballotMessage}
          {Boolean(isOwner) && (
            <div className="mt-1 flex items-center gap-1 text-xs font-medium text-neutral-400">
              <CrownIcon className="h-4 w-4 text-neutral-500" />
              <span className="text-neutral-500">{copy.leagueOwner}</span>
            </div>
          )}
        </Header>
      </div>
      {showScore && (
        <div className="mx-6 mt-4 mb-1 flex items-start justify-between font-medium">
          <div className="flex items-center">
            {rank === 1 && (score as number) > 0 && (
              <TrophyIcon className="mr-2 inline h-5 w-5" />
            )}
            {objectiveState.didCeremonyEnd && rank === 1 ? (
              copy.Winner.toUpperCase()
            ) : (
              <>
                {/* TODO: figure out spanish ordinals */}
                {getNumberSuffix(rank as number)} Place
              </>
            )}
          </div>{' '}
          {scoreIsStrict ? (
            <div className="text-rose-500">
              {score} / {Object.keys(oscarDatabase.nomineeCategories).length}
            </div>
          ) : (
            <div className="text-right">
              <div className="text-rose-500">
                {pluralize({
                  count: score as number,
                  singular: copy.point,
                })}
              </div>
              {objectiveState.didCeremonyEnd && (
                <div className="text-sm text-neutral-400">
                  {strictScore} /{' '}
                  {Object.keys(oscarDatabase.nomineeCategories).length}{' '}
                  {copy.correct}
                </div>
              )}
            </div>
          )}
        </div>
      )}
      {Boolean(allEnums?.users?.[ballotKey]?.letterboxdId) && (
        <LetterboxdBar
          userId={ballotKey}
          letterboxdId={allEnums?.users?.[ballotKey]?.letterboxdId!}
          className="mx-4 mt-4"
        />
      )}
      {Boolean(allEnums?.users?.[ballotKey]?.handle) && (
        <FavoritesBar
          userId={ballotKey}
          userHandle={allEnums?.users?.[ballotKey]?.handle!}
          className="mx-4 mt-4"
        />
      )}
      <BallotPreview
        ballot={ballot}
        userHandle={allEnums?.users?.[ballotKey]?.handle || ''}
        showScore={showScore}
      />
    </>
  );
};

let ViewBallotModal = ({
  ballotKey,
  isOpen,
  onClose,
  score,
  strictScore,
  scoreIsStrict,
  rank,
  isOwner,
}: {
  ballotKey: string;
  isOpen: boolean;
  onClose: () => void;
  scoreIsStrict?: boolean;
  score?: number;
  strictScore?: number;
  rank?: number;
  isOwner?: boolean;
}) => (
  <Modal isOpen={isOpen} onClose={onClose} disableBodyPadding>
    <Suspense fallback={<Loader />}>
      <ViewBallotModalContent
        ballotKey={ballotKey}
        score={score}
        strictScore={strictScore}
        scoreIsStrict={scoreIsStrict}
        rank={rank}
        isOwner={isOwner}
      />
    </Suspense>
  </Modal>
);

export default ViewBallotModal;
